import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import AssessmentBuilder from "./AssessmentBuilder";
import AssessmentTaker from "./AssessmentTaker";
import AssessmentReport from "./AssessmentReport";
import { PreAssessmentExplainer } from "./AssessmentExplainer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Unified Assessment Hub — routes between Builder, Explainer, Taker, and Report views.
 * Supports URL params: ?take={sessionId} to jump directly into taker.
 */
const AssessmentHub = ({ session }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState("builder");
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeResult, setActiveResult] = useState(null);
  const [selectedOffering, setSelectedOffering] = useState(null);

  // Read URL params on mount to support deep-linking from pipeline
  useEffect(() => {
    const takeId = searchParams.get("take");
    if (takeId) {
      setActiveSessionId(takeId);
      setView("taker");
      // Clean the URL param
      searchParams.delete("take");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Pre-assessment explainer before starting
  if (view === "explainer" && selectedOffering) {
    return (
      <div className="max-w-4xl mx-auto py-6" data-testid="assessment-explainer-view">
        <PreAssessmentExplainer
          offering={selectedOffering}
          onStart={() => {
            if (activeSessionId) {
              setView("taker");
            }
          }}
          onBack={() => {
            setSelectedOffering(null);
            setView("builder");
          }}
        />
      </div>
    );
  }

  if (view === "taker" && activeSessionId) {
    return (
      <AssessmentTaker
        session={session}
        sessionId={activeSessionId}
        onComplete={(result) => {
          setActiveResult(result);
          setView("report");
        }}
        onBack={() => {
          setActiveSessionId(null);
          setView("builder");
        }}
      />
    );
  }

  if (view === "report" && activeResult) {
    return (
      <AssessmentReport
        session={session}
        resultId={activeResult}
        onBack={() => {
          setActiveResult(null);
          setView("builder");
        }}
      />
    );
  }

  return (
    <AssessmentBuilder
      session={session}
      onStartAssessment={(sessionId, offering) => {
        setActiveSessionId(sessionId);
        if (offering) {
          setSelectedOffering(offering);
          setView("explainer");
        } else {
          setView("taker");
        }
      }}
      onViewResult={(result) => {
        setActiveResult(result);
        setView("report");
      }}
    />
  );
};

export default AssessmentHub;
